import React, { useCallback, useEffect, useState } from "react";
import { hotels, surveys, orders as ordersApi } from "../api/endpoints";
import SearchableSelect from "../components/SearchableSelect";
import type { ContractSpec, ManagementCompany } from "../types";

/* ------------------------------------------------------------------ */
/*  Helpers                                                           */
/* ------------------------------------------------------------------ */

interface SurveyOption {
  id: number;
  name: string;
  is_active: boolean;
}

interface PaymentTypeOption {
  id: number;
  name: string;
}

type FormData = Omit<ContractSpec, "id" | "survey_name">;

const emptyForm = (): FormData => ({
  management_company_id: 0,
  survey_id: null,
  contract_start_date: null,
  contract_end_date: null,
  discount_rate_percentage: 0,
  administrative_fee: 0,
  is_automatic_reshop: false,
  minimum_passing_score: 0,
  reshop_within_months: 0,
  shop_window_months: 0,
  payment_type_id: null,
  payment_terms_days: 0,
  late_payment_rate: 0,
  is_automatic_payment_assumed: false,
  is_purchase_order_required: false,
  send_invoices_to: "",
  call_result_email_notifications: "",
  catering_call_result_email_notifications: "",
  pass_completion_mail: "",
  fail_completion_mail: "",
  termination_mail: "",
  survey_pdf_notes: "",
  notes: "",
  is_active: true,
});

function specToForm(spec: ContractSpec): FormData {
  const { id: _id, survey_name: _sn, ...rest } = spec;
  return rest;
}

/* ------------------------------------------------------------------ */
/*  Collapsible Section                                               */
/* ------------------------------------------------------------------ */

function Section({
  title,
  defaultOpen = true,
  children,
}: {
  title: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex w-full items-center justify-between px-5 py-3 bg-slate-50 hover:bg-slate-100 transition-colors"
      >
        <span className="label-caps">{title}</span>
        <svg
          className={`h-4 w-4 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && <div className="p-5 space-y-4">{children}</div>}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Validation helpers                                                */
/* ------------------------------------------------------------------ */

interface ValidationErrors {
  [key: string]: string;
}

function validate(form: FormData): ValidationErrors {
  const errors: ValidationErrors = {};
  if (!form.management_company_id) errors.management_company_id = "Management company is required";
  if (form.discount_rate_percentage < 0 || form.discount_rate_percentage > 100)
    errors.discount_rate_percentage = "Must be between 0 and 100";
  if (form.administrative_fee < 0) errors.administrative_fee = "Must be non-negative";
  if (form.minimum_passing_score < 0 || form.minimum_passing_score > 100)
    errors.minimum_passing_score = "Must be between 0 and 100";
  if (form.payment_terms_days < 0) errors.payment_terms_days = "Must be non-negative";
  if (form.late_payment_rate < 0) errors.late_payment_rate = "Must be non-negative";
  if (form.reshop_within_months < 0) errors.reshop_within_months = "Must be non-negative";
  if (form.shop_window_months < 0) errors.shop_window_months = "Must be non-negative";
  if (
    form.contract_start_date &&
    form.contract_end_date &&
    form.contract_start_date > form.contract_end_date
  )
    errors.contract_end_date = "End date must be after start date";
  return errors;
}

/* ------------------------------------------------------------------ */
/*  Toast                                                             */
/* ------------------------------------------------------------------ */

function Toast({ message, type, onClose }: { message: string; type: "success" | "error"; onClose: () => void }) {
  useEffect(() => {
    const timer = setTimeout(onClose, 4000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div
      className={`fixed bottom-6 right-6 z-[60] flex items-center gap-3 rounded-xl px-5 py-3 shadow-lg text-sm font-medium rise-in ${
        type === "success"
          ? "bg-emerald-600 text-white"
          : "bg-red-600 text-white"
      }`}
    >
      {message}
      <button onClick={onClose} className="ml-2 opacity-70 hover:opacity-100">&times;</button>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Main Page Component                                               */
/* ------------------------------------------------------------------ */

export default function ContractSpecsPage() {
  /* --- Lookup data --- */
  const [companies, setCompanies] = useState<ManagementCompany[]>([]);
  const [surveyOptions, setSurveyOptions] = useState<SurveyOption[]>([]);
  const [paymentTypes, setPaymentTypes] = useState<PaymentTypeOption[]>([]);

  /* --- State --- */
  const [selectedMcId, setSelectedMcId] = useState<number>(0);
  const [specs, setSpecs] = useState<ContractSpec[]>([]);
  const [loading, setLoading] = useState(false);

  /* --- Modal --- */
  const [showModal, setShowModal] = useState(false);
  const [editSpec, setEditSpec] = useState<ContractSpec | null>(null);
  const [form, setForm] = useState<FormData>(emptyForm());
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [saving, setSaving] = useState(false);

  /* --- Toast --- */
  const [toast, setToast] = useState<{ message: string; type: "success" | "error" } | null>(null);

  /* --- Load lookups on mount --- */
  useEffect(() => {
    hotels.managementCompanies.list("limit=1000").then((res) => setCompanies(res.items));
    surveys.templates().then(setSurveyOptions);
    ordersApi.paymentTypes().then(setPaymentTypes);
  }, []);

  /* --- Load specs when MC changes --- */
  const loadSpecs = useCallback(() => {
    if (!selectedMcId) {
      setSpecs([]);
      return;
    }
    setLoading(true);
    hotels.contractSpecs
      .list(selectedMcId)
      .then(setSpecs)
      .catch(() => setToast({ message: "Failed to load contract specs", type: "error" }))
      .finally(() => setLoading(false));
  }, [selectedMcId]);

  useEffect(() => {
    loadSpecs();
  }, [loadSpecs]);

  /* --- Form helpers --- */
  const setField = <K extends keyof FormData>(key: K, value: FormData[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const openCreate = () => {
    setEditSpec(null);
    const newForm = emptyForm();
    newForm.management_company_id = selectedMcId;
    setForm(newForm);
    setErrors({});
    setShowModal(true);
  };

  const openEdit = (spec: ContractSpec) => {
    setEditSpec(spec);
    setForm(specToForm(spec));
    setErrors({});
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validationErrors = validate(form);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setSaving(true);
    try {
      if (editSpec) {
        await hotels.contractSpecs.update(editSpec.id, form);
        setToast({ message: "Contract spec updated successfully", type: "success" });
      } else {
        await hotels.contractSpecs.create(form as FormData & { management_company_id: number });
        setToast({ message: "Contract spec created successfully", type: "success" });
      }
      setShowModal(false);
      loadSpecs();
    } catch (err: any) {
      setToast({ message: err?.message || "Failed to save contract spec", type: "error" });
    } finally {
      setSaving(false);
    }
  };

  /* --- Derived --- */
  const selectedMcName = companies.find((c) => c.id === selectedMcId)?.name || "";
  const activeSpecs = specs.filter((s) => s.is_active);
  const inactiveSpecs = specs.filter((s) => !s.is_active);

  /* --- Render helpers --- */
  const fieldError = (key: string) =>
    errors[key] ? (
      <p className="text-xs text-red-500 mt-1">{errors[key]}</p>
    ) : null;

  /* ---------------------------------------------------------------- */
  /*  JSX                                                             */
  /* ---------------------------------------------------------------- */

  return (
    <div className="space-y-6">
      {/* Header */}
      <section className="surface-panel rise-in p-5 md:p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div className="min-w-0">
            <h1 className="text-2xl font-semibold tracking-tight text-slate-950">
              Contract Specifications
            </h1>
            <p className="mt-1 text-sm text-slate-500">
              Manage contract terms, pricing, reshop rules, and notification settings per management company.
            </p>
          </div>

          {selectedMcId > 0 && (
            <div className="grid gap-3 sm:grid-cols-3">
              <div className="kpi-card">
                <div className="eyebrow">Total Specs</div>
                <div className="mt-2 text-2xl font-semibold tracking-tight text-slate-950">
                  {specs.length}
                </div>
              </div>
              <div className="kpi-card">
                <div className="eyebrow">Active</div>
                <div className="mt-2 text-2xl font-semibold tracking-tight text-slate-950">
                  {activeSpecs.length}
                </div>
              </div>
              <div className="kpi-card">
                <div className="eyebrow">Inactive</div>
                <div className="mt-2 text-2xl font-semibold tracking-tight text-slate-950">
                  {inactiveSpecs.length}
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* MC Selector */}
      <section className="surface-panel rise-in delay-1 p-5 md:p-6">
        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr),auto]">
          <div>
            <label className="label-caps mb-2 block">Management Company</label>
            <SearchableSelect
              options={companies.map((mc) => ({ value: mc.id, label: mc.name }))}
              value={selectedMcId}
              onChange={(v) => setSelectedMcId(Number(v))}
              placeholder="-- Select a management company --"
              className="select-shell"
            />
          </div>
          {selectedMcId > 0 && (
            <div className="flex items-end">
              <button onClick={openCreate} className="button-primary">
                + New Contract Spec
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Spec list */}
      {selectedMcId > 0 && (
        <section className="surface-panel rise-in delay-2 p-5 md:p-6">
          {loading ? (
            <div className="text-center py-12 text-slate-400">Loading...</div>
          ) : specs.length === 0 ? (
            <div className="empty-state">
              <div className="text-center">
                <div className="label-caps">No contract specs for this HMC</div>
                <p className="mt-2 text-sm text-slate-500 max-w-md mx-auto">
                  {selectedMcName} does not have any contract specifications yet.
                </p>
                <button onClick={openCreate} className="button-primary mt-4">
                  Create First Contract Spec
                </button>
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="table-modern">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Survey</th>
                    <th>Contract Period</th>
                    <th>Discount %</th>
                    <th>Admin Fee</th>
                    <th>Min Score</th>
                    <th>Auto Reshop</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {specs.map((spec) => (
                    <tr key={spec.id} className="table-row-button">
                      <td className="font-mono text-sm font-semibold text-slate-900">#{spec.id}</td>
                      <td className="font-semibold text-slate-900">{spec.survey_name || "--"}</td>
                      <td className="text-sm text-slate-600">
                        {spec.contract_start_date
                          ? new Date(spec.contract_start_date).toLocaleDateString()
                          : "--"}{" "}
                        &mdash;{" "}
                        {spec.contract_end_date
                          ? new Date(spec.contract_end_date).toLocaleDateString()
                          : "--"}
                      </td>
                      <td>{spec.discount_rate_percentage}%</td>
                      <td>${Number(spec.administrative_fee).toFixed(2)}</td>
                      <td>{spec.minimum_passing_score}</td>
                      <td>
                        <span
                          className={`badge-pill ${
                            spec.is_automatic_reshop
                              ? "bg-emerald-100 text-emerald-800"
                              : "bg-slate-100 text-slate-600"
                          }`}
                        >
                          {spec.is_automatic_reshop ? "Yes" : "No"}
                        </span>
                      </td>
                      <td>
                        <span
                          className={`badge-pill ${
                            spec.is_active
                              ? "bg-emerald-100 text-emerald-800"
                              : "bg-red-100 text-red-700"
                          }`}
                        >
                          {spec.is_active ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td>
                        <button
                          onClick={() => openEdit(spec)}
                          className="button-ghost text-xs"
                        >
                          Edit
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {/* No MC selected hint */}
      {selectedMcId === 0 && (
        <section className="surface-panel rise-in delay-2 p-5 md:p-6">
          <div className="empty-state">
            <div className="text-center">
              <div className="label-caps">Select a Management Company</div>
              <p className="mt-2 text-sm text-slate-500 max-w-md mx-auto">
                Choose a management company from the dropdown above to view and manage its contract specifications.
              </p>
            </div>
          </div>
        </section>
      )}

      {/* ============================================================ */}
      {/* Create / Edit Modal                                          */}
      {/* ============================================================ */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-start justify-center z-50 overflow-y-auto py-10">
          <div className="surface-panel w-full max-w-3xl mx-4 rise-in">
            <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
              <h2 className="text-lg font-semibold text-slate-950">
                {editSpec ? "Edit Contract Spec" : "New Contract Spec"}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="button-ghost text-slate-400 hover:text-slate-700"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleSubmit} className="px-6 py-5 space-y-5 max-h-[75vh] overflow-y-auto">
              {/* ---- Contract Details ---- */}
              <Section title="Contract Details" defaultOpen={true}>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label className="label-caps mb-1 block">Management Company</label>
                    <SearchableSelect
                      options={companies.map((mc) => ({ value: mc.id, label: mc.name }))}
                      value={form.management_company_id}
                      onChange={(v) => setField("management_company_id", Number(v))}
                      placeholder="-- Select --"
                      disabled={!!editSpec}
                    />
                    {fieldError("management_company_id")}
                  </div>

                  <div>
                    <label className="label-caps mb-1 block">Survey</label>
                    <select
                      value={form.survey_id ?? ""}
                      onChange={(e) =>
                        setField("survey_id", e.target.value ? Number(e.target.value) : null)
                      }
                      className="select-shell"
                    >
                      <option value="">-- None --</option>
                      {surveyOptions.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="label-caps mb-1 block">Contract Start Date</label>
                    <input
                      type="date"
                      value={form.contract_start_date ?? ""}
                      onChange={(e) =>
                        setField("contract_start_date", e.target.value || null)
                      }
                      className="input-shell"
                    />
                  </div>

                  <div>
                    <label className="label-caps mb-1 block">Contract End Date</label>
                    <input
                      type="date"
                      value={form.contract_end_date ?? ""}
                      onChange={(e) =>
                        setField("contract_end_date", e.target.value || null)
                      }
                      className="input-shell"
                    />
                    {fieldError("contract_end_date")}
                  </div>
                </div>

                <div>
                  <label className="label-caps mb-1 block">Notes</label>
                  <textarea
                    value={form.notes}
                    onChange={(e) => setField("notes", e.target.value)}
                    className="textarea-shell"
                    rows={3}
                    placeholder="General notes about this contract spec..."
                  />
                </div>

                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="is_active"
                    checked={form.is_active}
                    onChange={(e) => setField("is_active", e.target.checked)}
                    className="h-4 w-4 rounded border-slate-300"
                  />
                  <label htmlFor="is_active" className="text-sm font-medium text-slate-700">
                    Active
                  </label>
                </div>
              </Section>

              {/* ---- Pricing & Payment ---- */}
              <Section title="Pricing & Payment" defaultOpen={false}>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  <div>
                    <label className="label-caps mb-1 block">Discount Rate (%)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      value={form.discount_rate_percentage}
                      onChange={(e) => setField("discount_rate_percentage", Number(e.target.value))}
                      className="input-shell"
                    />
                    {fieldError("discount_rate_percentage")}
                  </div>

                  <div>
                    <label className="label-caps mb-1 block">Administrative Fee ($)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={form.administrative_fee}
                      onChange={(e) => setField("administrative_fee", Number(e.target.value))}
                      className="input-shell"
                    />
                    {fieldError("administrative_fee")}
                  </div>

                  <div>
                    <label className="label-caps mb-1 block">Payment Type</label>
                    <select
                      value={form.payment_type_id ?? ""}
                      onChange={(e) =>
                        setField("payment_type_id", e.target.value ? Number(e.target.value) : null)
                      }
                      className="select-shell"
                    >
                      <option value="">-- None --</option>
                      {paymentTypes.map((pt) => (
                        <option key={pt.id} value={pt.id}>
                          {pt.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="label-caps mb-1 block">Payment Terms (days)</label>
                    <input
                      type="number"
                      min="0"
                      value={form.payment_terms_days}
                      onChange={(e) => setField("payment_terms_days", Number(e.target.value))}
                      className="input-shell"
                    />
                    {fieldError("payment_terms_days")}
                  </div>

                  <div>
                    <label className="label-caps mb-1 block">Late Payment Rate (%)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={form.late_payment_rate}
                      onChange={(e) => setField("late_payment_rate", Number(e.target.value))}
                      className="input-shell"
                    />
                    {fieldError("late_payment_rate")}
                  </div>
                </div>

                <div className="flex flex-col gap-3 sm:flex-row sm:gap-6">
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="is_automatic_payment_assumed"
                      checked={form.is_automatic_payment_assumed}
                      onChange={(e) => setField("is_automatic_payment_assumed", e.target.checked)}
                      className="h-4 w-4 rounded border-slate-300"
                    />
                    <label htmlFor="is_automatic_payment_assumed" className="text-sm font-medium text-slate-700">
                      Automatic Payment Assumed
                    </label>
                  </div>

                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="is_purchase_order_required"
                      checked={form.is_purchase_order_required}
                      onChange={(e) => setField("is_purchase_order_required", e.target.checked)}
                      className="h-4 w-4 rounded border-slate-300"
                    />
                    <label htmlFor="is_purchase_order_required" className="text-sm font-medium text-slate-700">
                      Purchase Order Required
                    </label>
                  </div>
                </div>
              </Section>

              {/* ---- Reshop Rules ---- */}
              <Section title="Reshop Rules" defaultOpen={false}>
                <div className="grid gap-4 sm:grid-cols-3">
                  <div className="flex items-center gap-3 sm:col-span-3">
                    <input
                      type="checkbox"
                      id="is_automatic_reshop"
                      checked={form.is_automatic_reshop}
                      onChange={(e) => setField("is_automatic_reshop", e.target.checked)}
                      className="h-4 w-4 rounded border-slate-300"
                    />
                    <label htmlFor="is_automatic_reshop" className="text-sm font-medium text-slate-700">
                      Automatic Reshop
                    </label>
                  </div>

                  <div>
                    <label className="label-caps mb-1 block">Minimum Passing Score</label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={form.minimum_passing_score}
                      onChange={(e) => setField("minimum_passing_score", Number(e.target.value))}
                      className="input-shell"
                    />
                    {fieldError("minimum_passing_score")}
                  </div>

                  <div>
                    <label className="label-caps mb-1 block">Reshop Within (months)</label>
                    <input
                      type="number"
                      min="0"
                      value={form.reshop_within_months}
                      onChange={(e) => setField("reshop_within_months", Number(e.target.value))}
                      className="input-shell"
                    />
                    {fieldError("reshop_within_months")}
                  </div>
                </div>
              </Section>

              {/* ---- Scheduling ---- */}
              <Section title="Scheduling" defaultOpen={false}>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label className="label-caps mb-1 block">Shop Window (months)</label>
                    <input
                      type="number"
                      min="0"
                      value={form.shop_window_months}
                      onChange={(e) => setField("shop_window_months", Number(e.target.value))}
                      className="input-shell"
                    />
                    {fieldError("shop_window_months")}
                  </div>
                </div>
              </Section>

              {/* ---- Email Notifications ---- */}
              <Section title="Email Notifications" defaultOpen={false}>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="sm:col-span-2">
                    <label className="label-caps mb-1 block">Send Invoices To</label>
                    <input
                      type="text"
                      value={form.send_invoices_to}
                      onChange={(e) => setField("send_invoices_to", e.target.value)}
                      className="input-shell"
                      placeholder="email@example.com, email2@example.com"
                    />
                  </div>

                  <div>
                    <label className="label-caps mb-1 block">Call Result Email Notifications</label>
                    <input
                      type="text"
                      value={form.call_result_email_notifications}
                      onChange={(e) => setField("call_result_email_notifications", e.target.value)}
                      className="input-shell"
                      placeholder="Comma-separated emails"
                    />
                  </div>

                  <div>
                    <label className="label-caps mb-1 block">Catering Call Result Emails</label>
                    <input
                      type="text"
                      value={form.catering_call_result_email_notifications}
                      onChange={(e) =>
                        setField("catering_call_result_email_notifications", e.target.value)
                      }
                      className="input-shell"
                      placeholder="Comma-separated emails"
                    />
                  </div>
                </div>
              </Section>

              {/* ---- Email Templates ---- */}
              <Section title="Email Templates" defaultOpen={false}>
                <div className="space-y-4">
                  {/* Variable reference */}
                  <details className="surface-panel p-3 rounded">
                    <summary className="cursor-pointer text-xs font-semibold text-slate-600 select-none">
                      Available Template Variables (click to expand)
                    </summary>
                    <div className="mt-2 grid grid-cols-2 gap-x-6 gap-y-1 text-xs text-slate-600">
                      {[
                        ["sales_staff_name", "Full name of sales staff"],
                        ["sales_staff_first_name", "First name of sales staff"],
                        ["sales_staff_last_name", "Last name of sales staff"],
                        ["sales_staff_email", "Sales staff email"],
                        ["shopper_name", "Shopper/caller full name"],
                        ["shopper_first_name", "Shopper first name"],
                        ["shopper_last_name", "Shopper last name"],
                        ["hotel_name", "Hotel property name"],
                        ["hotel_code", "Hotel code"],
                        ["hotel_phone", "Hotel contact number"],
                        ["hotel_city", "Hotel city"],
                        ["hotel_state", "Hotel state"],
                        ["management_company", "Management company name"],
                        ["shop_id", "Shop/call ID"],
                        ["call_type", "Call type"],
                        ["service_level", "Service level"],
                        ["percent_achieved", "Score percentage"],
                        ["call_date", "Date call completed"],
                        ["scheduled_date", "Scheduled call date"],
                      ].map(([name, desc]) => (
                        <div key={name} className="flex items-baseline gap-1">
                          <code className="text-indigo-600 font-mono text-[11px] whitespace-nowrap">{`{{${name}}}`}</code>
                          <span className="text-slate-500">— {desc}</span>
                        </div>
                      ))}
                    </div>
                    <p className="mt-2 text-[11px] text-slate-400">
                      Use these variables in your templates. They will be replaced with actual values when emails are sent.
                    </p>
                  </details>

                  <div>
                    <label className="label-caps mb-1 block">Pass Completion Mail</label>
                    <textarea
                      value={form.pass_completion_mail}
                      onChange={(e) => setField("pass_completion_mail", e.target.value)}
                      className="textarea-shell font-mono text-sm"
                      rows={6}
                      placeholder="Hi {{sales_staff_name}},&#10;&#10;Your shop call at {{hotel_name}} has been reviewed..."
                    />
                  </div>

                  <div>
                    <label className="label-caps mb-1 block">Fail Completion Mail</label>
                    <textarea
                      value={form.fail_completion_mail}
                      onChange={(e) => setField("fail_completion_mail", e.target.value)}
                      className="textarea-shell font-mono text-sm"
                      rows={6}
                      placeholder="Hi {{sales_staff_name}},&#10;&#10;Your shop call at {{hotel_name}} has been reviewed..."
                    />
                  </div>

                  <div>
                    <label className="label-caps mb-1 block">Termination Mail</label>
                    <textarea
                      value={form.termination_mail}
                      onChange={(e) => setField("termination_mail", e.target.value)}
                      className="textarea-shell font-mono text-sm"
                      rows={6}
                      placeholder="Hi {{sales_staff_name}},&#10;&#10;You have received a shop call..."
                    />
                  </div>
                </div>
              </Section>

              {/* ---- PDF Notes ---- */}
              <Section title="PDF Notes" defaultOpen={false}>
                <div>
                  <label className="label-caps mb-1 block">Survey PDF Notes</label>
                  <textarea
                    value={form.survey_pdf_notes}
                    onChange={(e) => setField("survey_pdf_notes", e.target.value)}
                    className="textarea-shell"
                    rows={4}
                    placeholder="Notes included in the survey PDF report..."
                  />
                </div>
              </Section>
            </form>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 border-t border-slate-200 px-6 py-4">
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="button-ghost"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={(e) => handleSubmit(e as any)}
                disabled={saving}
                className="button-primary"
              >
                {saving ? "Saving..." : editSpec ? "Update Spec" : "Create Spec"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
}
